import "@/App.css";
import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { TierProvider } from "@/context/TierContext";
import { Header } from "@/components/Header";
import { useStoreInfo } from "@/context/StoreInfoContext";
import { initGA, trackPageView } from "@/lib/analytics";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { InstallBanner } from "@/components/InstallBanner";
const PushBanner = lazy(() => import("@/components/PushBanner").then((m) => ({ default: m.PushBanner })));
const ChatWidget = lazy(() => import("@/components/ChatWidget").then((m) => ({ default: m.ChatWidget })));
const AiShopperWidget = lazy(() => import("@/components/AiShopperWidget").then((m) => ({ default: m.AiShopperWidget })));
import { MobileBottomNav } from "@/components/MobileBottomNav";
import { Footer } from "@/components/Footer";
import { StoreInfoProvider, INFO_PAGES } from "@/context/StoreInfoContext";
import { Seo } from "@/components/Seo";
const InfoPage = lazy(() => import("@/pages/InfoPage"));
const LocalSeoPage = lazy(() => import("@/pages/LocalSeoPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
import { LOCAL_PAGES } from "@/lib/localPages";
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const OrderTracking = lazy(() => import("@/pages/OrderTracking"));
const NexiReturn = lazy(() => import("@/pages/NexiReturn"));
import Landing from "@/pages/Landing";
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Catalog = lazy(() => import("@/pages/Catalog"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const Cart = lazy(() => import("@/pages/Cart"));
const CheckoutSuccess = lazy(() => import("@/pages/CheckoutSuccess"));
const Wishlist = lazy(() => import("@/pages/Wishlist"));
const Reservations = lazy(() => import("@/pages/Reservations"));
const Profile = lazy(() => import("@/pages/Profile"));
const Orders = lazy(() => import("@/pages/Orders"));
const Admin = lazy(() => import("@/pages/Admin"));
import AuthCallback from "@/pages/AuthCallback";
const Wheel = lazy(() => import("@/pages/Wheel"));
const Arcade = lazy(() => import("@/pages/Arcade"));
const KlarnaReturn = lazy(() => import("@/pages/KlarnaReturn"));
const PayPalReturn = lazy(() => import("@/pages/PayPalReturn"));
const PersonalShopper = lazy(() => import("@/pages/PersonalShopper"));
const FlyerPage = lazy(() => import("@/pages/FlyerPage"));

// Widget secondari montati dopo il primo caricamento per non rallentare la pagina
function useIdleReady(delay = 2500) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return ready;
}

function AppShell() {
  const location = useLocation();
  const idle = useIdleReady();
  const { info } = useStoreInfo();
  useEffect(() => { if (info?.ga_measurement_id) initGA(info.ga_measurement_id); }, [info?.ga_measurement_id]);
  useEffect(() => { trackPageView(location.pathname + location.search); }, [location.pathname, location.search]);
  // Synchronous check for OAuth callback
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const hideHeader = ["/login", "/register", "/forgot-password", "/reset-password"].includes(location.pathname);
  const showBottomNav = !hideHeader && !location.pathname.startsWith("/admin");
  const privatePath = hideHeader || ["/admin", "/profile", "/cart", "/orders", "/checkout"].some((p) => location.pathname.startsWith(p));
  return (
    <div className={showBottomNav ? "pb-16 md:pb-0" : ""}>
      {privatePath && <Seo noindex title="Area riservata" id="private" />}
      {!hideHeader && <InstallBanner />}
      {idle && !hideHeader && <Suspense fallback={null}><PushBanner /></Suspense>}
      {!hideHeader && <Header />}
      <Suspense fallback={<div className="min-h-[60vh] bg-[#0A0A0A]" />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/catalog" element={<Catalog />} />
        <Route path="/product/:id" element={<ProductDetail />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/checkout/klarna-return" element={<KlarnaReturn />} />
        <Route path="/checkout/paypal-return" element={<ProtectedRoute><PayPalReturn /></ProtectedRoute>} />
        <Route path="/personal-shopper" element={<PersonalShopper />} />
        <Route path="/flyer" element={<FlyerPage />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/ordine" element={<OrderTracking />} />
        <Route path="/checkout/nexi-return" element={<NexiReturn />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/wishlist" element={<ProtectedRoute><Wishlist /></ProtectedRoute>} />
        <Route path="/reservations" element={<ProtectedRoute><Reservations /></ProtectedRoute>} />
        <Route path="/reservations/new" element={<ProtectedRoute><Reservations /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
        <Route path="/wheel" element={<ProtectedRoute><Wheel /></ProtectedRoute>} />
        <Route path="/arcade" element={<ProtectedRoute><Arcade /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute staffOnly><Admin /></ProtectedRoute>} />
        {INFO_PAGES.map((p) => <Route key={p.slug} path={p.path} element={<InfoPage />} />)}
        {LOCAL_PAGES.map((p) => <Route key={p.slug} path={p.path} element={<LocalSeoPage />} />)}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
      {showBottomNav && <Footer />}
      {idle && <Suspense fallback={null}><ChatWidget /><AiShopperWidget /></Suspense>}
      {showBottomNav && <MobileBottomNav />}
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <TierProvider>
          <CartProvider>
            <BrowserRouter>
              <StoreInfoProvider>
                <AppShell />
              </StoreInfoProvider>
              <Toaster theme="dark" position="top-right" />
            </BrowserRouter>
          </CartProvider>
        </TierProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
